/**
 * Stage 5 — rivers along hex EDGES.
 *
 * Rivers live on the corner lattice: every hex corner is a vertex shared by three hexes, every hex
 * edge a lattice edge between two vertices. Water drains over this lattice:
 *  1. Vertex height = mean elevation of its three hexes (+ a little noise so flats meander).
 *  2. A priority-flood from all *outlets* (vertices touching sea or lake) gives every inland vertex
 *     a downstream parent. Only vertices whose three hexes are all land are ever entered, so every
 *     drainage edge runs between two land hexes; depressions are filled, so all land drains out.
 *  3. Rainfall (moisture, more in the highlands) is accumulated downstream.
 *  4. Edges whose accumulated flow exceeds a threshold are rivers. The threshold is searched so the
 *     river network has a target length; short systems and stubby side-branches are pruned.
 * Tributaries merge naturally (flows add up) and the width class grows with the flow.
 */
import { Dir, EDGE_CORNERS, HEX_SIZE, type HexGrid, opposite } from '../core/hex';
import type { Rng } from '../core/rng';
import type { RiverPath } from '../core/types';
import { clamp, isLand, type MapContext } from './context';
import { MinHeap } from './heap';
import { Noise } from './noise';

/**
 * Corner lattice. Vertex `2h` is the top corner (5) of hex h, `2h + 1` its bottom corner (2); every
 * other corner of the board is one of these seen from a neighbouring hex.
 */
export interface Lattice {
  readonly count: number;
  /** Three incident hexes per vertex (-1 = off board). */
  readonly hexes: Int32Array;
  /** Up to three adjacent vertices per vertex (-1 = none). */
  readonly adj: Int32Array;
  /** The hex edge each lattice edge runs along: hex + direction (the other hex is its neighbour). */
  readonly edgeHex: Int32Array;
  readonly edgeDir: Int8Array;
}

/** Canonical vertex id of corner `corner` of hex h, or -1 if the vertex has no owner on the board. */
export function vertexOf(grid: HexGrid, h: number, corner: number): number {
  switch (corner) {
    case 5:
      return 2 * h;
    case 2:
      return 2 * h + 1;
    case 0: {
      const ne = grid.neighbour(h, Dir.NE);
      return ne < 0 ? -1 : 2 * ne + 1;
    }
    case 1: {
      const se = grid.neighbour(h, Dir.SE);
      return se < 0 ? -1 : 2 * se;
    }
    case 3: {
      const sw = grid.neighbour(h, Dir.SW);
      return sw < 0 ? -1 : 2 * sw;
    }
    default: {
      const nw = grid.neighbour(h, Dir.NW);
      return nw < 0 ? -1 : 2 * nw + 1;
    }
  }
}

export function buildLattice(grid: HexGrid): Lattice {
  const count = grid.count * 2;
  const hexes = new Int32Array(count * 3).fill(-1);
  const adj = new Int32Array(count * 3).fill(-1);
  const edgeHex = new Int32Array(count * 3).fill(-1);
  const edgeDir = new Int8Array(count * 3).fill(-1);
  const degree = new Uint8Array(count);
  for (let h = 0; h < grid.count; h++) {
    hexes[2 * h * 3] = h;
    hexes[2 * h * 3 + 1] = grid.neighbour(h, Dir.NW);
    hexes[2 * h * 3 + 2] = grid.neighbour(h, Dir.NE);
    hexes[(2 * h + 1) * 3] = h;
    hexes[(2 * h + 1) * 3 + 1] = grid.neighbour(h, Dir.SW);
    hexes[(2 * h + 1) * 3 + 2] = grid.neighbour(h, Dir.SE);
  }
  // Directions E, NE, NW visit every interior hex edge exactly once.
  for (let h = 0; h < grid.count; h++) {
    for (let d = 0; d < 3; d++) {
      if (grid.neighbour(h, d) < 0) continue;
      const [ca, cb] = EDGE_CORNERS[d];
      const a = vertexOf(grid, h, ca);
      const b = vertexOf(grid, h, cb);
      if (a < 0 || b < 0) continue;
      for (const [v, w] of [
        [a, b],
        [b, a],
      ]) {
        const slot = v * 3 + degree[v]++;
        adj[slot] = w;
        edgeHex[slot] = h;
        edgeDir[slot] = d;
      }
    }
  }
  return { count, hexes, adj, edgeHex, edgeDir };
}

/**
 * World position of a lattice vertex: corner 5 (top) or 2 (bottom) of hex v >> 1. Computed as
 * centre ± HEX_SIZE rather than via grid.corner's cos/sin so it is exact on every JS engine.
 */
export function vertexPosition(grid: HexGrid, v: number): { x: number; y: number } {
  const h = v >> 1;
  return { x: grid.cx(h), y: grid.cy(h) + (v & 1 ? HEX_SIZE : -HEX_SIZE) };
}

export interface RiverResult {
  paths: RiverPath[];
  /** Widest river class (0 = none) running along any edge of each hex — used for city siting. */
  bankWidth: Uint8Array;
  /** 1 for land hexes at a river mouth. */
  mouth: Uint8Array;
  /** Number of independent river systems (distinct mouths). */
  systems: number;
}

// Vertex kinds (0 = unusable: off board or surrounded by water).
const INTERIOR = 1;
const OUTLET = 2;
/** Side branches shorter than this many edges are pruned (they read as noise). */
const MIN_TRIBUTARY = 3;
/** Systems whose main stem is shorter than this are dropped. */
const MIN_SYSTEM = 6;

export function carveRivers(ctx: MapContext, rng: Rng): RiverResult {
  const { grid, terrain, elevation, moisture } = ctx;
  const lat = buildLattice(grid);
  const V = lat.count;
  const meander = new Noise(rng);

  // 1. vertex classes, heights and rainfall
  const kind = new Uint8Array(V);
  const height = new Float64Array(V);
  const rain = new Float64Array(V);
  for (let v = 0; v < V; v++) {
    let water = 0;
    let land = 0;
    let sum = 0;
    let wet = 0;
    let waterLevel = Infinity;
    for (let k = 0; k < 3; k++) {
      const h = lat.hexes[v * 3 + k];
      if (h < 0) {
        land = -99;
        break;
      }
      if (isLand(terrain[h])) {
        land++;
        sum += elevation[h];
        wet += moisture[h];
      } else {
        water++;
        waterLevel = Math.min(waterLevel, elevation[h]);
      }
    }
    if (land <= 0) continue;
    const p = vertexPosition(grid, v);
    if (water > 0) {
      kind[v] = OUTLET;
      height[v] = waterLevel;
    } else {
      kind[v] = INTERIOR;
      // Low-frequency relief (wavelength ≈ 4 hexes) bends rivers across flats instead of letting the
      // flood carve ruler-straight channels to the nearest outlet.
      height[v] = sum / 3 + 7 * meander.fbm(p.x * 0.004, p.y * 0.004, 3);
      // Highlands catch most of the rain, so sources sit in hills and mountains, not in lowland fields.
      rain[v] = 0.15 + wet / 6 + 1.6 * clamp((sum / 3 - 85) / 110, 0, 1);
    }
  }

  // 2. priority-flood drainage
  const parent = new Int32Array(V).fill(-1);
  const parentSlot = new Int8Array(V).fill(-1);
  const filled = new Float64Array(V);
  const done = new Uint8Array(V);
  const order: number[] = [];
  const heap = new MinHeap(V);
  for (let v = 0; v < V; v++) {
    if (kind[v] !== OUTLET) continue;
    filled[v] = height[v];
    done[v] = 1;
    heap.push(v, height[v]);
  }
  while (heap.size > 0) {
    const v = heap.pop();
    for (let k = 0; k < 3; k++) {
      const w = lat.adj[v * 3 + k];
      if (w < 0 || done[w] || kind[w] !== INTERIOR) continue;
      done[w] = 1;
      filled[w] = Math.max(height[w], filled[v] + 1e-3);
      parent[w] = v;
      for (let j = 0; j < 3; j++) if (lat.adj[w * 3 + j] === v) parentSlot[w] = j;
      order.push(w);
      heap.push(w, filled[w]);
    }
  }

  // 3. flow accumulation (reverse flood order visits children before parents)
  const acc = new Float64Array(V);
  for (const v of order) acc[v] = rain[v];
  for (let i = order.length - 1; i >= 0; i--) {
    const v = order[i];
    const p = parent[v];
    if (kind[p] === INTERIOR) acc[p] += acc[v];
  }

  // 4. threshold search for the target network length
  let landCount = 0;
  for (let h = 0; h < grid.count; h++) if (isLand(terrain[h])) landCount++;
  const targetEdges = Math.round(landCount * rng.range(0.09, 0.115));
  const maxSystems = Math.round(clamp(landCount / 280, 4, 12));
  const sortedAcc = order.map((v) => acc[v]).sort((a, b) => b - a);
  const select = (threshold: number): Uint8Array =>
    selectRivers(order, parent, kind, acc, threshold, maxSystems);
  let lo = 0;
  let hi = Math.min(sortedAcc.length - 1, targetEdges * 6);
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const count = select(sortedAcc[mid]).reduce((s, x) => s + x, 0);
    if (count < targetEdges) lo = mid + 1;
    else hi = mid;
  }
  const threshold = sortedAcc[lo] ?? Infinity;
  const river = select(threshold);

  return buildOutput(ctx, lat, order, parent, parentSlot, kind, acc, river, threshold);
}

/**
 * River edges (v → parent[v]) for a flow threshold, after pruning stubby tributaries, dropping
 * short systems and keeping only the `maxSystems` largest.
 */
function selectRivers(
  order: number[],
  parent: Int32Array,
  kind: Uint8Array,
  acc: Float64Array,
  threshold: number,
  maxSystems: number,
): Uint8Array {
  const V = parent.length;
  const river = new Uint8Array(V);
  const upLen = new Int32Array(V);
  const mainChild = new Int32Array(V).fill(-1);
  for (const v of order) if (acc[v] >= threshold) river[v] = 1;
  // Upstream length and main (largest-flow) child, children first.
  for (let i = order.length - 1; i >= 0; i--) {
    const v = order[i];
    if (!river[v]) continue;
    upLen[v] += 1;
    const p = parent[v];
    if (upLen[v] > upLen[p]) upLen[p] = upLen[v];
    if (mainChild[p] < 0 || acc[v] > acc[mainChild[p]]) mainChild[p] = v;
  }
  // Prune short side-branches (parents first so removal cascades upstream).
  for (const v of order) {
    if (!river[v]) continue;
    const p = parent[v];
    if ((kind[p] === INTERIOR && !river[p]) || (mainChild[p] !== v && upLen[v] < MIN_TRIBUTARY)) river[v] = 0;
  }
  // Systems: identified by their mouth vertex (the last interior vertex before the outlet).
  const system = new Int32Array(V).fill(-1);
  const mouths: number[] = [];
  for (const v of order) {
    if (!river[v]) continue;
    const p = parent[v];
    if (kind[p] === OUTLET) {
      system[v] = v;
      if (upLen[v] >= MIN_SYSTEM) mouths.push(v);
    } else system[v] = system[p];
  }
  mouths.sort((a, b) => acc[b] - acc[a] || a - b);
  const keep = new Uint8Array(V);
  for (let i = 0; i < Math.min(maxSystems, mouths.length); i++) keep[mouths[i]] = 1;
  for (const v of order) if (river[v] && !keep[system[v]]) river[v] = 0;
  return river;
}

function widthClass(flow: number, threshold: number): number {
  const r = flow / threshold;
  return r < 2.2 ? 1 : r < 5 ? 2 : r < 11 ? 3 : 4;
}

/** Writes `ctx.riverEdges` and decomposes the network into source→mouth polylines. */
function buildOutput(
  ctx: MapContext,
  lat: Lattice,
  order: number[],
  parent: Int32Array,
  parentSlot: Int8Array,
  kind: Uint8Array,
  acc: Float64Array,
  river: Uint8Array,
  threshold: number,
): RiverResult {
  const { grid, riverEdges, terrain } = ctx;
  const V = lat.count;
  const bankWidth = new Uint8Array(grid.count);
  const mouth = new Uint8Array(grid.count);
  const firstChild = new Int32Array(V).fill(-1);
  const nextSibling = new Int32Array(V).fill(-1);
  const mouths: number[] = [];
  for (const v of order) {
    if (!river[v]) continue;
    const slot = v * 3 + parentSlot[v];
    const h = lat.edgeHex[slot];
    const d = lat.edgeDir[slot];
    const m = grid.neighbour(h, d);
    riverEdges[h] |= 1 << d;
    riverEdges[m] |= 1 << opposite(d);
    const w = widthClass(acc[v], threshold);
    bankWidth[h] = Math.max(bankWidth[h], w);
    bankWidth[m] = Math.max(bankWidth[m], w);
    const p = parent[v];
    if (kind[p] === OUTLET) {
      mouths.push(v);
      for (let k = 0; k < 3; k++) {
        const x = lat.hexes[p * 3 + k];
        if (isLand(terrain[x])) mouth[x] = 1;
      }
    } else {
      nextSibling[v] = firstChild[p];
      firstChild[p] = v;
    }
  }
  mouths.sort((a, b) => acc[b] - acc[a] || a - b);

  // Follow the largest-flow child upstream from v.
  const mainStem = (v: number): number[] => {
    const stem = [v];
    for (let c = v; ; ) {
      let best = -1;
      for (let ch = firstChild[c]; ch >= 0; ch = nextSibling[ch])
        if (best < 0 || acc[ch] > acc[best]) best = ch;
      if (best < 0) break;
      stem.push(best);
      c = best;
    }
    return stem;
  };

  const paths: RiverPath[] = [];
  for (const m of mouths) {
    // Queue of [first vertex of a stem, vertex it drains into]; trunk first, then tributaries.
    const queue: [number, number][] = [[m, parent[m]]];
    for (let qi = 0; qi < queue.length; qi++) {
      const [start, end] = queue[qi];
      const stem = mainStem(start);
      const vertices = stem.slice().reverse();
      vertices.push(end);
      const points: number[] = [];
      const widths: number[] = [];
      for (let i = 0; i < vertices.length; i++) {
        const p = vertexPosition(grid, vertices[i]);
        points.push(p.x, p.y);
        widths.push(i < vertices.length - 1 ? widthClass(acc[vertices[i]], threshold) : widths[i - 1]);
      }
      paths.push({ points, widths });
      for (let i = 0; i < stem.length; i++) {
        const next = stem[i + 1] ?? -1;
        for (let ch = firstChild[stem[i]]; ch >= 0; ch = nextSibling[ch])
          if (ch !== next) queue.push([ch, stem[i]]);
      }
    }
  }
  return { paths, bankWidth, mouth, systems: mouths.length };
}
